import { useState, useEffect } from "react";
import type { ElementType } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Sparkles, CreditCard, BrainCircuit, PieChart, LayoutDashboard, CalendarDays, FlaskConical, ArrowRight, Command, Landmark, Trash2, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscriptions } from "@/context/SubscriptionContext";

export const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { subscriptions, clearData } = useSubscriptions();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "f") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === "Escape") setIsOpen(false);
    };
    
    const handleCustomOpen = () => setIsOpen(true);

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("open-command-palette", handleCustomOpen);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("open-command-palette", handleCustomOpen);
    };
  }, []);

  const runCommand = async (path: string, action?: () => void | Promise<void>) => {
    setIsOpen(false);
    setQuery("");
    if (action) {
      await action();
    }
    if (path) {
      navigate(path);
    }
  };

  const openAIChat = () => {
    document.dispatchEvent(new CustomEvent('open-ai-chat'));
  };

  type CommandAction = {
    icon: ElementType;
    label: string;
    sub: string;
    path: string;
    color: string;
    action?: () => void | Promise<void>;
  };

  const baseActions: CommandAction[] = [
    { icon: Sparkles, label: "Ask Orizonte AI...", sub: "Conversational Concierge", path: "", action: openAIChat, color: "text-orizonte-lime" },
    { icon: LayoutDashboard, label: "Go to Overview", sub: "Navigation", path: "/dashboard", color: "text-orizonte-text" },
    { icon: CreditCard, label: "Manage Subscriptions", sub: "Navigation", path: "/subscriptions", color: "text-orizonte-text" },
    { icon: CalendarDays, label: "View Renewals Timeline", sub: "Navigation", path: "/calendar", color: "text-orizonte-text" },
    { icon: Landmark, label: "Manage Connected Accounts", sub: "Integrations", path: "/accounts", color: "text-orizonte-text" },
    { icon: Inbox, label: "Open Decision Inbox", sub: "Intent Memory", path: "/decision-inbox", color: "text-orizonte-lime" },
    { icon: FlaskConical, label: "Simulate Stack Changes", sub: "Sandbox", path: "/scenarios", color: "text-blue-400" },
    { icon: BrainCircuit, label: "View Stack Diagnostics", sub: "Navigation", path: "/intelligence", color: "text-orizonte-text" },
    { icon: PieChart, label: "Analyze Financial Telemetry", sub: "Navigation", path: "/analytics", color: "text-orizonte-text" },
    { icon: Trash2, label: "Clear All Subscriptions", sub: "System", path: "", action: () => { if (window.confirm("Delete all subscription data?")) clearData(); }, color: "text-red-400" }
  ];

  // Dynamically map active subscriptions into the command palette
  const subscriptionActions: CommandAction[] = subscriptions.map(sub => ({
    icon: CreditCard,
    label: `Manage ${sub.name}`,
    sub: `Subscription • $${sub.price}/${sub.cycle.substring(0, 2)}`,
    path: "/subscriptions", 
    color: "text-orizonte-textMuted"
  }));

  const allActions = [...baseActions, ...subscriptionActions];

  const filteredActions = allActions.filter(action => 
    action.label.toLowerCase().includes(query.toLowerCase()) || 
    action.sub.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: "-50%", y: -20 }}
            animate={{ opacity: 1, scale: 1, x: "-50%", y: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: "-50%", y: -20 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-[10%] md:top-[15%] z-[60] w-[95%] md:w-full max-w-2xl overflow-hidden rounded-2xl border border-orizonte-border bg-orizonte-surface/95 backdrop-blur-xl shadow-2xl"
          >
            <div className="flex items-center gap-3 border-b border-orizonte-border px-3 sm:px-4 py-3 sm:py-4">
              <Search className="w-5 h-5 text-orizonte-textMuted shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a command or search..."
                className="flex-1 bg-transparent text-base text-orizonte-text placeholder:text-orizonte-textMuted focus:outline-none min-w-0"
              />
              <div className="hidden sm:flex items-center gap-1 text-xs text-orizonte-textMuted bg-white/[0.05] px-2 py-1 rounded border border-white/5 shrink-0">
                <Command className="w-3 h-3" /> K
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-1.5 sm:p-2">
              {filteredActions.length === 0 ? (
                <div className="py-12 text-center text-sm text-orizonte-textMuted">
                  No commands or subscriptions found.
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => runCommand(action.path, action.action)}
                      className="w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl hover:bg-white/[0.04] transition-colors group text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5 group-hover:bg-white/[0.08] transition-colors shrink-0">
                          <action.icon className={`w-4 h-4 ${action.color}`} />
                        </div>
                        <div className="min-w-0 truncate">
                          <div className="text-sm font-medium text-orizonte-text truncate">{action.label}</div>
                          <div className="text-xs text-orizonte-textMuted truncate">{action.sub}</div>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-orizonte-textMuted opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="bg-white/[0.02] border-t border-orizonte-border px-4 py-3 flex items-center gap-4 text-xs text-orizonte-textMuted">
              <div className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-orizonte-lime" /> AI routing active</div>
              <div className="hidden sm:block w-1 h-1 rounded-full bg-orizonte-border" />
              <div className="hidden sm:block">Press ESC to close</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
